// ═══════════════════════════════════════════════════════════════════════════
// targetTicketRead.js — turn what a model read off a photographed green ticket
// into a DRAFT of the entry form, plus an honest list of what it could not fill.
//
// It never saves and it never completes a field it could not read. The output
// lands in the form the person is already looking at, with the gaps named, and
// they press Save. That is the same shape as the basis-sheet importer, which
// extracts and reports and leaves the destructive half a reviewed step.
//
// THREE THINGS IT DELIBERATELY WILL NOT DO:
//
//  1. IT NEVER SETS A FUTURES PRICE. The futures target is cash - basis against
//     the LIVE board, derived at save time. A ticket photographed in March
//     carries the futures number that was right in March, and transcribing it
//     would put a stale order back into the system through the one door built
//     to catch stale orders.
//
//  2. IT NEVER GUESSES THE DELIVERY POINT. Five of ten photographed tickets had
//     none written; the field decides the basis and was worth up to 34c
//     ($3,740 on one ticket). A "+15" on the paper does not identify it either —
//     Batavia, LeRoy and Avon were all about +0.15 in March and now sit 34c
//     apart. Unread means unread.
//
//  3. IT NEVER MATCHES A FARMER LOOSELY. An exact, case-insensitive, whole-name
//     match to one customer is a fact; anything else is left for the human to
//     pick. A grain customer need not be in the insurance book at all, so a
//     miss is normal and must not become a wrong id silently attached to a
//     grower's order.
//
// THE WINDOW MUST RESOLVE ON THE BOARD OR STAY BLANK. The form's shipment
// picker is bound to (ship_month|cme_contract) cells the board actually
// carries for that point, commodity and bid structure. A window the board does
// not print cannot be selected, so writing one in would produce a form that
// looks filled and cannot save. It comes back as a gap naming what IS carried —
// the elevator collapses and splits its windows routinely (8-D replaced wheat's
// 26-Sep with Aug-Sep), and that list is what makes the gap fixable in one move.
// ═══════════════════════════════════════════════════════════════════════════

const { normalizeDeliveryWindow, parseBasisCents, q, unq } = require("./cashTargetBasis");
const { slugForMarket, slugsForTown, DELIVERY_TOWN_SLUGS } = require("./basisMarketAliases");

const str = (v) => (v == null ? "" : String(v).trim());
const numOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(str(v)) && !isNaN(Date.parse(str(v)));

// A cash price on a WNY grain ticket. Wide enough for $21 organic beans and
// tight enough that a bushels figure typed into the price line is refused
// rather than saved as a price.
const CASH_MIN = 0.5, CASH_MAX = 40;

// ── WHICH NUMBER IS THE CASH PRICE IS DECIDED BY ARITHMETIC ────────────────
// A price line prints TWO prices — futures first under "CBOT", then the cash
// price the word "Cash" labels — and the futures figure is usually written a
// SECOND time on the "Target @:" line above. So the futures number appears
// twice on the sheet and the cash number once, and a reader going by
// appearance picks the wrong one.
//
// It did. Kevin, 2026-08-26: a $12.25 cash target at -39 basis reads $12.64
// futures on the paper; the reader returned $12.25's line as cash = 12.64 and
// the form then derived 12.64 - (-0.39) = $13.03. The error COMPOUNDS — the
// basis is added twice — and it lands in the one direction this whole tool
// exists to prevent: an order resting 39c away from what the grower agreed to.
//
// Telling the model harder is not a fix, because the sheet genuinely shows the
// futures number more often than the cash one. The ticket's own identity is:
//
//     cash = futures + basis
//
// and for any basis other than exactly zero AT MOST ONE assignment of two
// candidate numbers satisfies it. Verified over the real book and Kevin's own
// ticket: cash=12.64/fut=12.25 gives 11.86, wrong; cash=12.25/fut=12.64 gives
// 12.25, exact. So the assignment is DETERMINED — this is an identity with a
// unique solution, not a story that happens to fit the numbers.
//
// The degenerate case is basis exactly 0 with two equal prices, where both
// assignments hold and name the same number, so nothing is at stake.
//
// Returns null when NEITHER assignment reconciles. That is a pen slip or a
// misread digit, and picking one would invent the grower's instruction — the
// same rule evaluateCashTarget already applies to a saved ticket that does not
// balance.
function assignTicketPrices(a, b, basis) {
  const has = (v) => v != null && Number.isFinite(v);
  if (!has(a) && !has(b)) return { cash: null, futures: null, how: "none" };

  // Only one price legible: it cannot be checked against anything.
  if (has(a) !== has(b)) {
    const only = has(a) ? a : b;
    const fromFutures = has(a) && !has(b);   // caller passes futures first
    if (has(basis)) {
      // A lone FUTURES price still yields the cash price exactly.
      if (fromFutures) return { cash: unq(q(only) + q(basis)), futures: only, how: "derived_from_futures" };
      return { cash: only, futures: unq(q(only) - q(basis)), how: "cash_only" };
    }
    return { cash: fromFutures ? null : only, futures: fromFutures ? only : null,
             how: fromFutures ? "futures_only_no_basis" : "cash_only_no_basis" };
  }

  // Both legible but no basis to check them with: the labelled reading stands,
  // unverified.
  if (!has(basis)) return { cash: b, futures: a, how: "unverified" };

  if (q(a) + q(basis) === q(b)) return { cash: b, futures: a, how: "as_written" };
  if (q(b) + q(basis) === q(a)) return { cash: a, futures: b, how: "transposed" };
  return null;
}

/**
 * @param {object} parsed  the model's JSON (see targetTicketPrompt)
 * @param {object} ctx
 * @param {Array}  ctx.board       grain_basis_board rows
 * @param {Array}  [ctx.farmers]   [{id, name}] for an exact-match only
 * @param {Array}  [ctx.points]    delivery points the form is offering
 * @param {string} [ctx.today]     YYYY-MM-DD
 * @returns {{draft: object, rungs: Array, gaps: Array<string>, seen: object,
 *            basisCheck: object|null}}
 */
function readTargetTicket(parsed, ctx = {}) {
  const p = parsed || {};
  const board = Array.isArray(ctx.board) ? ctx.board : [];
  const farmers = Array.isArray(ctx.farmers) ? ctx.farmers : [];
  const today = ctx.today || new Date().toISOString().slice(0, 10);
  const gaps = [];

  // Whatever the model said it could not read is carried through verbatim and
  // FIRST — it is the most reliable statement in the payload.
  for (const u of Array.isArray(p.unreadable) ? p.unreadable : []) {
    const s = str(u);
    if (s) gaps.push(s);
  }

  // ── farmer: exact match or nothing ──────────────────────────────────────
  const farmerName = str(p.farmer_name);
  let farmerId = "", matchedName = "";
  if (farmerName) {
    const key = farmerName.toLowerCase();
    const hits = farmers.filter((x) => str(x.name).toLowerCase() === key);
    if (hits.length === 1) { farmerId = hits[0].id || ""; matchedName = str(hits[0].name); }
  } else if (!gaps.some((g) => /farmer|name/i.test(g))) {
    gaps.push("the farmer's name");
  }

  // ── commodity: must be one the board prices ─────────────────────────────
  const boardCrops = [...new Set(board.map((b) => str(b.commodity).toLowerCase()).filter(Boolean))];
  const readCrop = str(p.commodity).toLowerCase();
  let commodity = "";
  if (readCrop && boardCrops.includes(readCrop)) commodity = readCrop;
  else if (readCrop) gaps.push(`the crop reads "${str(p.commodity)}", which the board does not price`);
  else if (!gaps.some((g) => /crop|commodit/i.test(g))) gaps.push("the crop");

  // ── bid structure ───────────────────────────────────────────────────────
  const readStruct = str(p.bid_structure).toLowerCase();
  const bidStructure = readStruct === "pickup" || readStruct === "delivered" ? readStruct : "";
  if (!bidStructure && !gaps.some((g) => /delivered|pick|structure/i.test(g))) {
    gaps.push("whether it is delivered or picked up");
  }

  // ── delivery point: matched against what the form offers, never guessed ──
  const readPoint = str(p.delivery_point);
  const offered = Array.isArray(ctx.points) ? ctx.points : [];
  let deliveryPoint = "";
  if (readPoint) {
    const hit = offered.find((o) => str(o).toLowerCase() === readPoint.toLowerCase());
    if (hit) deliveryPoint = hit;
    else {
      // Try the town map before giving up — the ticket may say "Leroy" where
      // the board column is "LeRoy & TD Ag". Only a town the map knows counts;
      // this is membership, never fuzzy.
      const viaTown = Object.keys(DELIVERY_TOWN_SLUGS)
        .find((t) => t.toLowerCase() === readPoint.toLowerCase());
      if (viaTown && offered.some((o) => str(o).toLowerCase() === viaTown.toLowerCase())) {
        deliveryPoint = offered.find((o) => str(o).toLowerCase() === viaTown.toLowerCase());
      } else {
        gaps.push(`the delivery point reads "${readPoint}", which is not one the board bids` +
                  (offered.length ? ` (it carries ${offered.join(", ")})` : ""));
      }
    }
  } else if (!gaps.some((g) => /delivery point/i.test(g))) {
    gaps.push("the delivery point — it decides the basis, so it has to be picked");
  }

  // ── the shipment window, resolved against the board ─────────────────────
  const readWindow = str(p.shipment_period);
  const readCme = str(p.cme_month).toUpperCase();
  let deliveryWindow = "", boardCell = null;
  if (deliveryPoint && commodity && bidStructure) {
    const want = new Set(slugsForTown(deliveryPoint) ||
      [slugForMarket(deliveryPoint)].filter(Boolean));
    const cells = board.filter((b) => str(b.commodity).toLowerCase() === commodity &&
      str(b.term).toLowerCase() === bidStructure && b.basis != null &&
      want.has(slugForMarket(b.location)));
    const wantWin = normalizeDeliveryWindow(readWindow);
    const hits = cells.filter((b) =>
      (wantWin && normalizeDeliveryWindow(b.ship_month) === wantWin) &&
      (!readCme || str(b.cme_contract).toUpperCase() === readCme));
    // Dedupe by the cell the picker itself keys on, so two columns covering one
    // town do not read as a disagreement the person has to resolve.
    const byCell = new Map();
    for (const h of hits) byCell.set(`${h.ship_month}|${h.cme_contract}`, h);
    if (byCell.size === 1) {
      boardCell = [...byCell.values()][0];
      deliveryWindow = `${boardCell.ship_month}|${boardCell.cme_contract}`;
    } else {
      // IN THE SHEET'S OWN ROW ORDER, and any remainder COUNTED. Sorted
      // alphabetically and sliced, this list dropped "Aug-Sep" — which is
      // precisely the window 8-D collapsed 26-Sep into, i.e. the answer.
      const seenCell = new Set();
      const carried = cells
        .slice()
        .sort((a, b) => (a.ship_sort ?? 0) - (b.ship_sort ?? 0))
        .map((b) => `${b.ship_month} / ${b.cme_contract}`)
        .filter((k) => (seenCell.has(k) ? false : (seenCell.add(k), true)));
      const shownCells = carried.slice(0, 8);
      const carriedText = carried.length
        ? ` — it carries ${shownCells.join(" · ")}` +
          (carried.length > shownCells.length ? ` · +${carried.length - shownCells.length} more` : "")
        : "";
      gaps.push(readWindow || readCme
        ? `the shipment period reads "${[readWindow, readCme].filter(Boolean).join(" / ")}", ` +
          `which is not on the current sheet` + carriedText
        : "the shipment period");
    }
  } else if (!readWindow && !gaps.some((g) => /shipment/i.test(g))) {
    gaps.push("the shipment period");
  }

  // ── order structure and the locked basis ────────────────────────────────
  const readOrder = str(p.order_structure).toLowerCase();
  const basisLocked = readOrder === "basis";
  const orderType = readOrder === "hta" ? "hta" : "cash_target";

  // basis_written is cents as the form writes it by hand: "+15", "-6".
  const basisWritten = str(p.basis_written).replace(/[¢c]/gi, "");
  const writtenBasis = parseBasisCents(basisWritten);
  let lockedBasisInput = "";
  if (basisLocked) {
    if (writtenBasis != null) lockedBasisInput = String(Math.round(writtenBasis * 100));
    else gaps.push("the locked basis — this ticket is on a basis contract, so the board cannot supply it");
  }

  // ── rungs ───────────────────────────────────────────────────────────────
  // The basis the ticket's OWN arithmetic used: what is written on it, or the
  // board's number for the cell it names. Either is a valid check; the written
  // one is preferred because it is what the person had in front of them.
  const basisForCheck = writtenBasis != null ? writtenBasis
    : (boardCell && boardCell.basis != null ? Number(boardCell.basis) : null);

  const rawRungs = Array.isArray(p.rungs) ? p.rungs : [];
  const rungs = [];
  rawRungs.forEach((r, i) => {
    const futuresRead = numOrNull(r && r.futures_written);
    const cashRead = numOrNull(r && r.cash_target);
    const bu = numOrNull(r && r.bushels);
    if (futuresRead == null && cashRead == null && bu == null) return;

    const picked = assignTicketPrices(futuresRead, cashRead, basisForCheck);
    if (picked === null) {
      // Neither assignment reconciles — a pen slip or a misread digit. The
      // price is LEFT BLANK rather than guessed: a wrong cash target rests at
      // the elevator until it fills, and this is the one number nobody would
      // catch by eye afterwards.
      gaps.push(`line ${i + 1} does not add up — the sheet reads ${futuresRead} futures and ` +
                `${cashRead} cash against basis ${basisForCheck}, and cash must equal futures ` +
                `plus basis. Type the cash price in yourself.`);
      rungs.push({ cashTarget: "", bushels: bu == null ? "" : String(bu) });
      if (bu == null) gaps.push(`the bushels on line ${i + 1}`);
      return;
    }

    const cash = picked.cash;
    if (cash != null && (cash < CASH_MIN || cash > CASH_MAX)) {
      gaps.push(`the price on line ${i + 1} reads ${cash}, which is not a cash price per bushel`);
      return;
    }
    // The reading was the wrong way round and the arithmetic caught it. SAID
    // OUT LOUD — a silent correction of a grower's price is not something to
    // discover later.
    if (picked.how === "transposed") {
      gaps.push(`line ${i + 1}: the two prices were read the wrong way round — ` +
                `$${cash} is the cash target and $${picked.futures} is the futures order ` +
                `(they reconcile at basis ${basisForCheck}). Corrected.`);
    } else if (picked.how === "derived_from_futures") {
      gaps.push(`line ${i + 1}: only the futures price was legible ($${picked.futures}), ` +
                `so the cash target is derived as $${cash} at basis ${basisForCheck}. Check it.`);
    } else if (picked.how === "cash_only" || picked.how === "cash_only_no_basis" ||
               picked.how === "unverified") {
      gaps.push(`line ${i + 1}: only one price could be checked, so $${cash} is taken as the ` +
                `CASH target on the reading alone. Confirm it is not the futures figure.`);
    } else if (picked.how === "futures_only_no_basis") {
      gaps.push(`line ${i + 1}: only a futures price was legible and there is no basis to ` +
                `convert it, so the cash target is blank.`);
    }

    if (cash == null && picked.how !== "futures_only_no_basis") gaps.push(`the cash price on line ${i + 1}`);
    if (bu == null) gaps.push(`the bushels on line ${i + 1}`);
    rungs.push({ cashTarget: cash == null ? "" : String(cash), bushels: bu == null ? "" : String(bu) });
  });
  if (!rungs.length) {
    rungs.push({ cashTarget: "", bushels: "" });
    if (!gaps.some((g) => /price|cash|bushel/i.test(g))) gaps.push("the cash price and bushels");
  }

  // ── the date ────────────────────────────────────────────────────────────
  const enteredOn = isIsoDate(p.entered_on) ? str(p.entered_on) : today;
  if (!isIsoDate(p.entered_on)) gaps.push(`the date it was written — defaulted to ${today}`);

  // ── the drift, reported at ENTRY time ───────────────────────────────────
  // Not a gap and not an error: a ticket written in March against +15 is a
  // correct record of March. But if it is being re-entered now and the board
  // has moved, the futures order this form derives will NOT be the number on
  // the paper, and saying so here is the whole product applied one step
  // earlier. Suppressed for a locked basis, which by contract does not move.
  let basisCheck = null;
  if (!basisLocked && boardCell && writtenBasis != null) {
    const boardBasis = Number(boardCell.basis);
    const drift = unq(q(boardBasis) - q(writtenBasis));
    basisCheck = {
      writtenBasis, boardBasis, drift,
      moved: q(boardBasis) !== q(writtenBasis),
      sheetVersion: boardCell.sheet_version || null,
    };
  }

  return {
    draft: {
      farmerId, farmerName: matchedName, farmerQuery: matchedName ? "" : farmerName,
      buyer: str(p.buyer), enteredOn,
      commodity, bidStructure, deliveryPoint, deliveryWindow,
      orderType, basisLocked, lockedBasisInput,
      notes: str(p.notes),
      contractNumber: str(p.contract_number),
    },
    rungs,
    gaps,
    basisCheck,
    // Everything the model claimed to see, kept so the review panel can show
    // the reading beside the form rather than only its own conclusions.
    seen: {
      farmerName, buyer: str(p.buyer), commodity: str(p.commodity),
      bidStructure: str(p.bid_structure), deliveryPoint: readPoint,
      shipmentPeriod: readWindow, cmeMonth: readCme,
      orderStructure: str(p.order_structure), basisWritten,
      rungs: rawRungs.length,
    },
  };
}

module.exports = { readTargetTicket, assignTicketPrices, CASH_MIN, CASH_MAX };
